/**
 * Calendar Invite Generator for HMC Events
 *
 * Generates Outlook-compatible .ics calendar invites for confirmed ticket requests.
 *
 * Features:
 * - Creates downloadable .ics file for Outlook import
 * - Includes event details, ticket confirmation, and instructions
 * - Styled body with Calibri 11 font
 * - Includes user's email signature
 */

import type { HMCEvent } from '../hmcEventsStore';

export interface CalendarInviteConfig {
  /** The confirmed event */
  event: HMCEvent;
  /** Guest's full name */
  guestName: string;
  /** Guest's email address */
  guestEmail: string;
  /** Number of tickets confirmed */
  ticketsConfirmed: number;
  /** Email address provided for AXS ticket transfer */
  axsEmail: string;
  /** Which option was confirmed (1 = Primary, 2 = Second, 3 = Third) */
  optionConfirmed: 1 | 2 | 3;
  /** User's email signature HTML (will be converted to plain text) */
  signatureHtml?: string;
  /** Organizer name */
  organizerName?: string;
  /** Organizer email */
  organizerEmail?: string;
}

/**
 * Get ordinal suffix for a number (1st, 2nd, 3rd, etc.)
 */
function getOrdinalSuffix(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
}

/**
 * Format option number as readable text
 */
function formatOptionText(option: 1 | 2 | 3): string {
  const labels = {
    1: 'primary choice',
    2: '2nd choice',
    3: '3rd choice',
  };
  return labels[option];
}

/**
 * Strip HTML tags and convert to plain text
 * Preserves line breaks and basic formatting
 */
function htmlToPlainText(html: string): string {
  if (!html) return '';

  return html
    // Convert <br> and </p> to newlines
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n\n')
    .replace(/<\/div>/gi, '\n')
    .replace(/<\/tr>/gi, '\n')
    // Remove all other HTML tags
    .replace(/<[^>]+>/g, '')
    // Decode HTML entities
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&apos;/g, "'")
    // Clean up multiple newlines
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * Format date for ICS file (YYYYMMDDTHHMMSS format)
 */
function formatICSDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${year}${month}${day}T${hours}${minutes}${seconds}`;
}

/**
 * Format date for display in email body
 */
function formatDisplayDate(date: Date): string {
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Parse a date string (YYYY-MM-DD) as a local date to avoid timezone shifts.
 *
 * IMPORTANT: JavaScript's `new Date("2025-04-09")` interprets date-only strings as UTC midnight,
 * which causes them to appear as the previous day in western timezones (e.g., April 9 UTC = April 8 CST).
 * This function parses date components directly to create a local date.
 */
function parseLocalDate(dateStr: string | null | undefined): Date | null {
  if (!dateStr) return null;
  try {
    // Handle YYYY-MM-DD format by parsing components
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      const [year, month, day] = parts.map(Number);
      if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
        return new Date(year, month - 1, day);
      }
    }
    // Fallback for other formats (shouldn't happen, but be safe)
    return new Date(dateStr);
  } catch {
    return null;
  }
}

/**
 * Escape special characters for ICS format
 * ICS requires escaping of commas, semicolons, and newlines
 */
function escapeICS(text: string): string {
  return text
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n');
}

/**
 * Fold long lines for ICS format (max 75 chars per line)
 */
function foldICSLine(line: string): string {
  const maxLength = 75;
  if (line.length <= maxLength) return line;

  const result: string[] = [];
  let remaining = line;
  let isFirst = true;

  while (remaining.length > 0) {
    const chunkLength = isFirst ? maxLength : maxLength - 1;
    const chunk = remaining.substring(0, chunkLength);
    remaining = remaining.substring(chunkLength);

    if (isFirst) {
      result.push(chunk);
      isFirst = false;
    } else {
      result.push(' ' + chunk);
    }
  }

  return result.join('\r\n');
}

/**
 * Clean event name (remove attendance info, time suffixes, etc.)
 */
function getCleanEventName(opponent: string | null): string {
  if (!opponent) return 'Event';
  const cleaned = opponent
    .replace(/\s*[-–]\s*\d+\s*(tickets?|tix|seats?).*$/i, '')
    .replace(/\s*\(\d+\s*(tickets?|tix|seats?)\).*$/i, '')
    // Remove time suffixes like "- EB 6:00", "- 7:00 PM", "- 6:00"
    .replace(/\s*[-–]\s*[A-Z]*\s*\d{1,2}:\d{2}\s*(AM|PM)?.*$/i, '')
    // Also remove suite assignment patterns like "(Scroggins)"
    .replace(/\s*\([^)]+\)\s*$/, '')
    .trim();
  return cleaned || 'Event';
}

/**
 * Parse event time string to hours and minutes
 */
function parseEventTime(timeStr: string | null): { hours: number; minutes: number } | null {
  if (!timeStr) return null;

  // Try parsing formats like "7:00 PM", "7:00PM", "19:00"
  const match12h = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
  if (match12h) {
    let hours = parseInt(match12h[1], 10);
    const minutes = parseInt(match12h[2], 10);
    const period = match12h[3].toUpperCase();

    if (period === 'PM' && hours !== 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;

    return { hours, minutes };
  }

  const match24h = timeStr.match(/(\d{1,2}):(\d{2})/);
  if (match24h) {
    return {
      hours: parseInt(match24h[1], 10),
      minutes: parseInt(match24h[2], 10),
    };
  }

  return null;
}

// Default email signature for Evan Roland
const DEFAULT_SIGNATURE_PLAIN = `Best Regards,

Evan Roland
USI Employee Benefits
281-748-4045 (mobile)`;

const DEFAULT_SIGNATURE_HTML = `<p>Best Regards,</p>
<p>Evan Roland<br/>
USI Employee Benefits<br/>
281-748-4045 (mobile)</p>`;

// Toyota Center event page URL
const TOYOTA_CENTER_EVENT_URL = 'https://www.toyotacenter.com/events/usi-suite';

/**
 * Generate the calendar invite body text
 */
function generateInviteBody(config: CalendarInviteConfig): string {
  const {
    event,
    guestName,
    ticketsConfirmed,
    axsEmail,
    optionConfirmed,
    signatureHtml = '',
  } = config;

  const eventName = getCleanEventName(event.opponent);
  const eventDate = parseLocalDate(event.event_date);
  const formattedDate = eventDate ? formatDisplayDate(eventDate) : 'Date TBD';

  // Build the body with the specified format
  const bodyLines = [
    `Hi ${guestName.split(' ')[0]},`,
    '',
    `Great news! Your ${formatOptionText(optionConfirmed)} has been confirmed for the ${eventName} on ${formattedDate}.`,
    '',
    `Tickets Confirmed: ${ticketsConfirmed}`,
    `Toyota Center Event Link: ${TOYOTA_CENTER_EVENT_URL}`,
    '',
    `I will be sending your tickets electronically via the AXS Mobile App to the email address that was submitted in your ticket request (${axsEmail}). Please check your account 3-4 days prior to the event which will include your tickets and parking pass.`,
    '',
    `You should also receive an email from AXS when the tickets are transferred - please check your spam folder if you do not see the email.`,
    '',
    `It is always my recommendation to arrive early when doors open to get the best seats.`,
    '',
    `As mentioned in the invite, all food, drinks, wine and beer are included.`,
    '',
    `If you have any questions, please feel free to reach out to me directly.`,
    '',
  ];

  // Add signature - use provided signature or default
  if (signatureHtml) {
    const signaturePlainText = htmlToPlainText(signatureHtml);
    if (signaturePlainText) {
      bodyLines.push(signaturePlainText);
    }
  } else {
    bodyLines.push(DEFAULT_SIGNATURE_PLAIN);
  }

  return bodyLines.join('\n');
}

/**
 * Generate X-ALT-DESC HTML body for rich text support in Outlook
 */
function generateHtmlBody(config: CalendarInviteConfig): string {
  const {
    event,
    guestName,
    ticketsConfirmed,
    axsEmail,
    optionConfirmed,
    signatureHtml = '',
  } = config;

  const eventName = getCleanEventName(event.opponent);
  const eventDate = parseLocalDate(event.event_date);
  const formattedDate = eventDate ? formatDisplayDate(eventDate) : 'Date TBD';

  // Use provided signature or default
  const finalSignatureHtml = signatureHtml || DEFAULT_SIGNATURE_HTML;

  // Build HTML body with Calibri 11 font
  const htmlBody = `<!DOCTYPE HTML PUBLIC "-//W3C//DTD HTML 3.2//EN">
<HTML>
<HEAD>
<META NAME="Generator" CONTENT="HMC Events Calendar Invite Generator">
<TITLE>${escapeHtml(eventName)}</TITLE>
</HEAD>
<BODY>
<div style="font-family: Calibri, sans-serif; font-size: 11pt; color: #333333;">
<p>Hi ${escapeHtml(guestName.split(' ')[0])},</p>

<p>Great news! Your <strong>${formatOptionText(optionConfirmed)}</strong> has been confirmed for the <strong>${escapeHtml(eventName)}</strong> on ${escapeHtml(formattedDate)}.</p>

<p><strong>Tickets Confirmed:</strong> ${ticketsConfirmed}<br/>
<strong>Toyota Center Event Link:</strong> <a href="${TOYOTA_CENTER_EVENT_URL}" style="color: #0066cc;">here</a></p>

<p>I will be sending your tickets electronically via the AXS Mobile App to the email address that was submitted in your ticket request (<span style="color: #0066cc;">${escapeHtml(axsEmail)}</span>). Please check your account 3-4 days prior to the event which will include your tickets and parking pass.</p>

<p>You should also receive an email from AXS when the tickets are transferred - please check your spam folder if you do not see the email.</p>

<p>It is always my recommendation to arrive early when doors open to get the best seats.</p>

<p>As mentioned in the invite, all food, drinks, wine and beer are included.</p>

<p>If you have any questions, please feel free to reach out to me directly.</p>

<br/>${finalSignatureHtml}
</div>
</BODY>
</HTML>`;

  return htmlBody;
}

/**
 * Escape HTML special characters
 */
function escapeHtml(text: string): string {
  const map: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;',
  };
  return text.replace(/[&<>"']/g, (char) => map[char] || char);
}

/**
 * Generate a unique identifier for ICS
 */
function generateUID(): string {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 11);
  return `${timestamp}-${random}@hmc-events.eroland.me`;
}

/**
 * Generate the ICS file content for an Outlook calendar invite
 */
export function generateCalendarInvite(config: CalendarInviteConfig): string {
  const { event } = config;

  const eventName = getCleanEventName(event.opponent);

  // Parse event date and time using parseLocalDate to avoid timezone shifts
  const eventDate = parseLocalDate(event.event_date) ?? new Date();

  // Set start time from event
  const eventTime = parseEventTime(event.kickoff_time || event.event_start_time);
  if (eventTime) {
    eventDate.setHours(eventTime.hours, eventTime.minutes, 0, 0);
  } else {
    // Default to 7:00 PM if no time specified
    eventDate.setHours(19, 0, 0, 0);
  }

  // End time is always 11:00 PM as specified
  const endDate = new Date(eventDate);
  endDate.setHours(23, 0, 0, 0);

  // Current timestamp for DTSTAMP
  const now = new Date();

  // Generate body content
  const plainTextBody = generateInviteBody(config);
  const htmlBody = generateHtmlBody(config);

  // Location - For Dynamo/Dash games, always show Shell Stadium
  const isDynamoOrDash = event.event_type === 'dynamo' || event.event_type === 'dash';
  const location = isDynamoOrDash ? 'Shell Stadium, Houston, TX' : (event.venue || 'Toyota Center, Houston, TX');

  // Build ICS content
  const icsLines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//HMC Events//Calendar Invite Generator//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    foldICSLine(`UID:${generateUID()}`),
    foldICSLine(`DTSTAMP:${formatICSDate(now)}`),
    foldICSLine(`DTSTART:${formatICSDate(eventDate)}`),
    foldICSLine(`DTEND:${formatICSDate(endDate)}`),
    foldICSLine(`SUMMARY:${escapeICS(eventName)}`),
    foldICSLine(`DESCRIPTION:${escapeICS(plainTextBody)}`),
    foldICSLine(`LOCATION:${escapeICS(location)}`),
    'STATUS:CONFIRMED',
    'SEQUENCE:0',
    'TRANSP:OPAQUE',
    // X-ALT-DESC for HTML body support in Outlook
    foldICSLine(`X-ALT-DESC;FMTTYPE=text/html:${escapeICS(htmlBody)}`),
    'END:VEVENT',
    'END:VCALENDAR',
  ];

  return icsLines.join('\r\n');
}

/**
 * Download the calendar invite as an .ics file
 */
export function downloadCalendarInvite(icsContent: string, eventName: string, guestName: string): void {
  const cleanEventName = eventName.replace(/[^a-zA-Z0-9\s-]/g, '').replace(/\s+/g, '-');
  const cleanGuestName = guestName.split(' ')[0].replace(/[^a-zA-Z0-9]/g, '');
  const filename = `${cleanEventName}-${cleanGuestName}-Invite.ics`;

  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
}

/**
 * Generate Outlook Web App URL for creating a calendar invite
 * Opens directly in Outlook with the event pre-filled
 * Uses HTML body with bold formatting and proper paragraph spacing
 *
 * Based on: F:/1 - Work - Daily Automated Apps/Outlook_Calendar_Availability_Reporter
 */
export function generateOutlookWebUrl(config: CalendarInviteConfig): string {
  const { event, guestName, guestEmail, ticketsConfirmed, axsEmail, optionConfirmed, signatureHtml = '' } = config;

  const eventName = getCleanEventName(event.opponent);

  // Parse event date and time using parseLocalDate to avoid timezone shifts
  const eventDate = parseLocalDate(event.event_date) ?? new Date();

  // Set start time from event
  const eventTime = parseEventTime(event.kickoff_time || event.event_start_time);
  if (eventTime) {
    eventDate.setHours(eventTime.hours, eventTime.minutes, 0, 0);
  } else {
    // Default to 7:00 PM if no time specified
    eventDate.setHours(19, 0, 0, 0);
  }

  // End time is always 11:00 PM
  const endDate = new Date(eventDate);
  endDate.setHours(23, 0, 0, 0);

  // Format dates for OWA URL (local time, NOT UTC - matches Python reference)
  // Uses local time components to avoid UTC conversion that toISOString() does
  const formatOWADate = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
  };

  // Build the HTML body with bold formatting (matching ICS format)
  const firstName = guestName.split(' ')[0];
  const formattedDate = eventDate.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  // Build HTML body with proper paragraph spacing and bold formatting
  const htmlParagraphs = [
    `Hi ${escapeHtml(firstName)},`,
    `Great news! Your <b>${formatOptionText(optionConfirmed)}</b> has been confirmed for the <b>${escapeHtml(eventName)}</b> on ${escapeHtml(formattedDate)}.`,
    `<b>Tickets Confirmed:</b> ${ticketsConfirmed}<br><b>Toyota Center Event Link:</b> <a href="${TOYOTA_CENTER_EVENT_URL}">here</a>`,
    `I will be sending your tickets electronically via the AXS Mobile App to the email address that was submitted in your ticket request (${escapeHtml(axsEmail)}). Please check your account 3-4 days prior to the event which will include your tickets and parking pass.`,
    `You should also receive an email from AXS when the tickets are transferred - please check your spam folder if you do not see the email.`,
    `It is always my recommendation to arrive early when doors open to get the best seats.`,
    `As mentioned in the invite, all food, drinks, wine and beer are included.`,
    `If you have any questions, please feel free to reach out to me directly.`,
  ];

  // Add signature - use provided or default
  const finalSignatureHtml = signatureHtml || DEFAULT_SIGNATURE_HTML;
  htmlParagraphs.push('<br>' + finalSignatureHtml);

  // Join with double line breaks for paragraph spacing
  const bodyHtml = htmlParagraphs.join('<br><br>');

  // Location - For Dynamo/Dash games, always show Shell Stadium
  const isDynamoOrDash = event.event_type === 'dynamo' || event.event_type === 'dash';
  const location = isDynamoOrDash ? 'Shell Stadium, Houston, TX' : (event.venue || 'Toyota Center, Houston, TX');

  // Build URL with proper encoding
  // OWA calendar compose URL format from Outlook_Calendar_Availability_Reporter
  const params = new URLSearchParams();
  params.set('subject', `${eventName} (USI Suite)`);
  params.set('startdt', formatOWADate(eventDate));
  params.set('enddt', formatOWADate(endDate));
  params.set('to', guestEmail);
  params.set('location', location);
  params.set('body', bodyHtml);

  return `https://outlook.office.com/calendar/action/compose?${params.toString()}`;
}

/**
 * Open Outlook Web App to create a calendar invite
 */
export function openOutlookCalendarInvite(config: CalendarInviteConfig): void {
  const url = generateOutlookWebUrl(config);
  window.open(url, '_blank');
}
